import { Fragment } from 'react';
import { Divider } from '@mui/material';
import {
  TextField,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
  Flex,
  Button,
  Autocomplete,
  IconButton,
} from '@elementary/common-ui/components';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import { WarehouseType } from '@elementary/common-ui/types';
import { capitalize } from '@elementary/common-ui/utils/common';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { EnvironmentForm } from 'shared/types';
import {
  getAssetSourceRules,
  getWarehouseTerms,
  readErrorMessage,
} from './utils';

type Props = {
  index: number;
  warehouseType: WarehouseType;
  isNameRequired: boolean;
  disabled?: boolean;
};

const WarehouseSourceFields = ({
  index,
  warehouseType,
  isNameRequired,
  disabled,
}: Props) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<EnvironmentForm>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `assetSources.${index}.dbFilters`,
  });

  const {
    databaseLabel,
    databaseNoun,
    databasePlaceholder,
    schemasNoun,
    schemaPlaceholder,
  } = getWarehouseTerms(warehouseType);
  const { hasWarehouseSchemas } = getAssetSourceRules(warehouseType);

  const sourceErrors = errors.assetSources?.[index];
  const nameError = readErrorMessage(sourceErrors?.name);
  const dbFiltersErrors =
    sourceErrors && 'dbFilters' in sourceErrors
      ? sourceErrors.dbFilters
      : undefined;

  return (
    <FlexColumn gap={2}>
      <FormField title="Asset source name" errorMessage={nameError}>
        <TextField
          {...register(
            `assetSources.${index}.name`,
            isNameRequired ? { required: requiredLabel } : undefined,
          )}
          variant="outlined"
          placeholder="e.g. jaffle_shop"
          disabled={disabled}
          size="large"
        />
      </FormField>

      {fields.map((field, filterIndex) => (
        <Fragment key={field.id}>
          {filterIndex > 0 && <Divider />}
          <Flex gap={1} sx={{ alignItems: 'flex-start' }}>
            <FlexColumn gap={2} sx={{ flex: 1 }}>
              <FormField
                title={databaseLabel}
                errorMessage={readErrorMessage(
                  Array.isArray(dbFiltersErrors)
                    ? dbFiltersErrors[filterIndex]?.database
                    : undefined,
                )}
              >
                <TextField
                  {...register(
                    `assetSources.${index}.dbFilters.${filterIndex}.database`,
                    { required: requiredLabel },
                  )}
                  variant="outlined"
                  placeholder={databasePlaceholder}
                  disabled={disabled}
                  size="large"
                />
              </FormField>
              {hasWarehouseSchemas && (
                <FormField
                  title={capitalize(schemasNoun)}
                  optional
                  description={`Leave empty to monitor all ${schemasNoun} in the ${databaseNoun}.`}
                >
                  <Controller
                    control={control}
                    name={`assetSources.${index}.dbFilters.${filterIndex}.schemas`}
                    render={({ field: schemasField }) => (
                      <Autocomplete
                        multiple
                        freeSolo
                        autoSelect
                        size="large"
                        options={[]}
                        placeholder={schemaPlaceholder}
                        disabled={disabled}
                        value={schemasField.value}
                        onChange={(_, next) => {
                          const schemas = (
                            Array.isArray(next) ? next : []
                          ).flatMap((item) =>
                            typeof item === 'string' && item.trim()
                              ? [item.trim()]
                              : [],
                          );
                          schemasField.onChange(schemas);
                        }}
                      />
                    )}
                  />
                </FormField>
              )}
            </FlexColumn>
            {fields.length > 1 && (
              <IconButton
                onClick={() => remove(filterIndex)}
                disabled={disabled}
                aria-label={`Remove ${databaseNoun}`}
              >
                <TrashIcon />
              </IconButton>
            )}
          </Flex>
        </Fragment>
      ))}

      <Button
        variant="outlined"
        color="secondary"
        startIcon={<PlusIcon />}
        onClick={() => append({ database: '', schemas: [] })}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        {`Add ${databaseNoun}`}
      </Button>
    </FlexColumn>
  );
};

export default WarehouseSourceFields;
